import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useCompanies } from './hooks';
import { useCurrentDomain } from './context/DomainContext';
import {
    getKioskAllowedCompanies,
    addKioskAllowedCompany,
    updateKioskAllowedCompany,
    deleteKioskAllowedCompany,
} from './services/masterDataService';
import { THEME } from './theme/themeColors';

const STATUS_ON  = { background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' };
const STATUS_OFF = { background: '#f8d7da', color: '#842029', border: '1px solid #f5c2c7' };

const cardHeaderStyle = {
    color: THEME.onPrimary, fontWeight: 600, fontSize: 14, padding: '8px', backgroundColor: THEME.primary,
};

const Field = ({ label, required, htmlFor }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <label htmlFor={htmlFor} style={{ fontSize: '10px', fontWeight: 600, color: 'black', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 0 }}>
            {label}
            {required && <span style={{ color: '#dc3545', marginLeft: '3px', fontSize: '14px', lineHeight: '1' }}>*</span>}
        </label>
    </div>
);

const Spinner = () => (
    <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm" style={{ color: THEME.primary }} role="status">
            <span className="visually-hidden">Loading...</span>
        </div>
    </div>
);

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Companies the Kiosk menu/feature is enabled for (dbo.mst_KioskAllowedCompany) — replaces
// the Doctor frontend's hardcoded KIOSK_ALLOWED_COMPANY_IDS array with a DB-driven list staff
// can manage here. "Delete" is a soft delete (Status=0, see deleteKioskAllowedCompany) — the
// row and its CompanyID are kept so re-enabling it later is just a status toggle.
const KioskAllowedCompanies = () => {
    const { domain } = useCurrentDomain();
    const { companies, loading: companiesLoading } = useCompanies(domain);

    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [companySearch, setCompanySearch] = useState('');
    const [rows, setRows] = useState([]);
    const [rowsLoading, setRowsLoading] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [search, setSearch] = useState('');
    const fetchSeqRef = useRef(0);

    useEffect(() => {
        setSelectedCompanies([]);
        setCompanySearch('');
        setSearch('');
    }, [domain]);

    const fetchRows = useCallback(async () => {
        const seq = ++fetchSeqRef.current;
        if (!domain) {
            setRows([]);
            setRowsLoading(false);
            return;
        }
        setRowsLoading(true);
        try {
            const data = await getKioskAllowedCompanies(domain);
            if (seq !== fetchSeqRef.current) return;
            setRows(data || []);
        } catch (err) {
            if (seq !== fetchSeqRef.current) return;
            toast.error('Failed to load allowed companies.');
        } finally {
            if (seq === fetchSeqRef.current) setRowsLoading(false);
        }
    }, [domain]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    const companyLabel = (companyId) => {
        const match = companies.find((c) => String(c.value) === String(companyId));
        return match ? match.label : companyId;
    };

    const handleAdd = async () => {
        if (selectedCompanies.length === 0) return;
        setAddLoading(true);
        const loadingToast = toast.loading('Adding companies...');
        try {
            const results = await Promise.allSettled(
                selectedCompanies.map((id) => addKioskAllowedCompany(id, domain))
            );
            const failed = results.filter((r) => r.status === 'rejected');
            const addedCount = results.length - failed.length;
            toast.dismiss(loadingToast);
            if (addedCount > 0) {
                toast.success(`${addedCount} ${addedCount === 1 ? 'company' : 'companies'} added to the allowed list.`);
            }
            if (failed.length > 0) {
                toast.error(failed[0].reason?.response?.data?.message || `Failed to add ${failed.length} ${failed.length === 1 ? 'company' : 'companies'}.`);
            }
            // Keep only the failed ones selected so they can be retried.
            setSelectedCompanies(selectedCompanies.filter((_, i) => results[i].status === 'rejected'));
            await fetchRows();
        } finally {
            setAddLoading(false);
        }
    };

    const handleToggleStatus = async (row) => {
        const enabling = !row.status;
        setBusyId(row.id);
        const loadingToast = toast.loading(enabling ? 'Enabling...' : 'Disabling...');
        try {
            if (enabling) {
                await updateKioskAllowedCompany(row.id, { status: true }, domain);
            } else {
                // Soft delete — sets Status=0 server-side, row and CompanyID are kept.
                await deleteKioskAllowedCompany(row.id, domain);
            }
            toast.dismiss(loadingToast);
            toast.success(enabling ? 'Company enabled.' : 'Company disabled.');
            await fetchRows();
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || 'Failed to update company.');
        } finally {
            setBusyId(null);
        }
    };

    const filteredRows = rows.filter((r) =>
        String(companyLabel(r.companyID)).toLowerCase().includes(search.toLowerCase())
    );

    // Excludes companies already in the list regardless of status — a disabled row's correct
    // path back is "Enable" on that row (right panel), not a second Add (the backend rejects a
    // duplicate CompanyID either way, but steering here avoids a confusing round-trip error).
    const existingCompanyIds = new Set(rows.map((r) => String(r.companyID)));
    const addableCompanies = companies.filter((c) => !existingCompanyIds.has(String(c.value)));
    const filteredAddableCompanies = addableCompanies.filter((c) =>
        c.label.toLowerCase().includes(companySearch.toLowerCase())
    );

    const thStyle = { padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' };

    return (
        <div className="container-fluid p-0 px-1">
            <div className="card-body">
                <div className="row g-2">
                    <div className="col-12 col-lg-5">
                        <div className="card border h-100" style={{ borderRadius: '6px' }}>
                            <div className="card-header" style={cardHeaderStyle}>
                                Add Allowed Company
                            </div>
                            <div className="card-body p-2">
                                <div className="mb-2">
                                    <Field label="Search Companies" htmlFor="kac-company-search" />
                                    <input
                                        id="kac-company-search"
                                        type="text"
                                        className="form-control form-control-sm kac-input"
                                        placeholder="Filter company list..."
                                        value={companySearch}
                                        onChange={(e) => setCompanySearch(e.target.value)}
                                        style={{ fontSize: '12px' }}
                                    />
                                </div>
                                <div style={{ height: '280px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', background: '#fafafa' }}>
                                    {companiesLoading ? (
                                        <Spinner />
                                    ) : filteredAddableCompanies.length === 0 ? (
                                        <div className="text-center py-4" style={{ color: '#adb5bd', fontSize: '12px' }}>
                                            No companies available.
                                        </div>
                                    ) : (
                                        filteredAddableCompanies.map((c) => {
                                            const checked = selectedCompanies.includes(c.value);
                                            return (
                                                <label
                                                    key={c.value}
                                                    className="kac-row"
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px',
                                                        borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '12px',
                                                        background: checked ? THEME.primaryLight : undefined,
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() =>
                                                            setSelectedCompanies((prev) =>
                                                                prev.includes(c.value)
                                                                    ? prev.filter((id) => id !== c.value)
                                                                    : [...prev, c.value]
                                                            )
                                                        }
                                                        style={{ accentColor: THEME.primary }}
                                                    />
                                                    <div style={{ flex: 1, minWidth: 0, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {c.label}
                                                    </div>
                                                </label>
                                            );
                                        })
                                    )}
                                </div>
                                <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>
                                    The Kiosk menu/feature will be enabled for the selected companies.
                                </div>
                                <div className="d-flex justify-content-center gap-2 mt-2">
                                    <button
                                        onClick={() => { setSelectedCompanies([]); setCompanySearch(''); }}
                                        className="btn btn-outline-secondary btn-sm px-3"
                                        style={{ fontSize: '12px' }}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        onClick={handleAdd}
                                        disabled={addLoading || rowsLoading || selectedCompanies.length === 0}
                                        className="btn btn-sm btn-theme px-3 fw-semibold"
                                        style={{ fontSize: '12px' }}
                                    >
                                        {addLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                Adding...
                                            </>
                                        ) : (
                                            `Add to Allowed List (${selectedCompanies.length})`
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-lg-7">
                        <div className="card border h-100" style={{ borderRadius: '6px' }}>
                            <div className="card-header" style={cardHeaderStyle}>
                                Allowed Companies
                            </div>
                            <div className="card-body p-2">
                                <div className="mb-2">
                                    <Field label="Search" htmlFor="kac-search" />
                                    <input
                                        id="kac-search"
                                        type="text"
                                        className="form-control form-control-sm kac-input"
                                        placeholder="Filter by company..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        style={{ fontSize: '12px' }}
                                    />
                                </div>
                                <div style={{ height: '360px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                                    {rowsLoading ? (
                                        <Spinner />
                                    ) : !domain ? (
                                        <div className="text-center py-4" style={{ color: '#adb5bd', fontSize: '12px' }}>
                                            Select a domain first.
                                        </div>
                                    ) : filteredRows.length === 0 ? (
                                        <div className="text-center py-4" style={{ color: '#adb5bd', fontSize: '12px' }}>
                                            {rows.length === 0 ? 'No allowed companies yet.' : `No companies match "${search}".`}
                                        </div>
                                    ) : (
                                        <table className="table table-sm table-hover mb-0 kac-table" style={{ fontSize: '12px' }}>
                                            <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                                                <tr>
                                                    <th style={thStyle}>Company</th>
                                                    <th style={thStyle}>Status</th>
                                                    <th style={thStyle}>Added</th>
                                                    <th style={thStyle}><span className="visually-hidden">Actions</span></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRows.map((row) => (
                                                    <tr key={row.id}>
                                                        <td style={{ padding: '6px 10px' }}>{companyLabel(row.companyID)}</td>
                                                        <td style={{ padding: '6px 10px' }}>
                                                            <span
                                                                style={{
                                                                    fontSize: '10px',
                                                                    fontWeight: 600,
                                                                    padding: '2px 8px',
                                                                    borderRadius: '20px',
                                                                    ...(row.status ? STATUS_ON : STATUS_OFF),
                                                                }}
                                                            >
                                                                {row.status ? 'Enabled' : 'Disabled'}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '6px 10px', color: '#6c757d' }}>{formatDate(row.createdAt)}</td>
                                                        <td style={{ padding: '6px 10px' }}>
                                                            <button
                                                                onClick={() => handleToggleStatus(row)}
                                                                disabled={busyId === row.id}
                                                                className={`btn btn-sm px-2 ${row.status ? 'btn-outline-danger' : 'btn-outline-success'}`}
                                                                style={{ fontSize: '11px' }}
                                                                aria-label={`${row.status ? 'Disable' : 'Enable'} ${companyLabel(row.companyID)}`}
                                                            >
                                                                {busyId === row.id ? (
                                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                                                ) : row.status ? (
                                                                    'Disable'
                                                                ) : (
                                                                    'Enable'
                                                                )}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .kac-row:hover { background: ${THEME.alpha(0.06)}; }
                .kac-input:focus { border-color: ${THEME.primary}; box-shadow: 0 0 0 0.2rem ${THEME.alpha(0.2)}; }
                .kac-table { --bs-table-hover-bg: ${THEME.alpha(0.05)}; }
            `}</style>
        </div>
    );
};

export default KioskAllowedCompanies;